import { differenceInCalendarDays } from "date-fns/differenceInCalendarDays";
import { parseISO } from "date-fns/parseISO";
import { startOfToday } from "date-fns/startOfToday";
import type { Identifier } from "ra-core";

import type { Contact, Tag, Task } from "../types";

export const PARTNER_STALE_DAYS = 30;
export const DEFAULT_STALE_DAYS = 21;

const EXCLUDED_TAGS = ["Dead", "Client", "Dormant", "Internal"];
const PARTNER_TAGS = ["Referral Source", "Channel"];

export type StaleSection = "never" | "partner" | "default";

export type StaleContact = {
  contact: Contact;
  section: StaleSection;
  daysSince: number | null;
};

const SECTION_ORDER: Record<StaleSection, number> = {
  never: 0,
  partner: 1,
  default: 2,
};

/**
 * Contacts nobody has talked to for too long.
 * - Never contacted: last_seen is null.
 * - Partners (Referral Source / Channel): stale after 30 days.
 * - Everyone else: stale after 21 days.
 * Hidden: tagged Dead / Client / Dormant / Internal, or with an open task due today or later
 * (an overdue task does not hide them).
 */
export const getStaleContacts = ({
  contacts,
  tags,
  tasks,
  today = startOfToday(),
}: {
  contacts: Contact[];
  tags: Tag[];
  tasks: Task[];
  today?: Date;
}): StaleContact[] => {
  const tagIdsNamed = (names: string[]) =>
    new Set(
      tags.filter((tag) => names.includes(tag.name)).map((tag) => tag.id),
    );
  const excludedTagIds = tagIdsNamed(EXCLUDED_TAGS);
  const partnerTagIds = tagIdsNamed(PARTNER_TAGS);

  const snoozedContactIds = new Set<Identifier>(
    tasks
      .filter(
        (task) =>
          !task.done_date &&
          task.due_date &&
          differenceInCalendarDays(parseISO(task.due_date), today) >= 0,
      )
      .map((task) => task.contact_id),
  );

  return contacts
    .filter(
      (contact) =>
        !(contact.tags ?? []).some((id) => excludedTagIds.has(id)) &&
        !snoozedContactIds.has(contact.id),
    )
    .map((contact): StaleContact | null => {
      if (!contact.last_seen) {
        return { contact, section: "never", daysSince: null };
      }
      const daysSince = differenceInCalendarDays(
        today,
        parseISO(contact.last_seen),
      );
      const isPartner = (contact.tags ?? []).some((id) =>
        partnerTagIds.has(id),
      );
      const threshold = isPartner ? PARTNER_STALE_DAYS : DEFAULT_STALE_DAYS;
      if (daysSince < threshold) return null;
      return { contact, section: isPartner ? "partner" : "default", daysSince };
    })
    .filter((item): item is StaleContact => item !== null)
    .sort(
      (a, b) =>
        SECTION_ORDER[a.section] - SECTION_ORDER[b.section] ||
        (b.daysSince ?? 0) - (a.daysSince ?? 0),
    );
};
